import type { FC, ReactNode } from 'react'

import { ArrowUpRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import RollText from '@/components/generals/roll-text'
import SectionLabel from '@/components/generals/section-label'
import SplitReveal from '@/components/motion/split-reveal'
import TransitionLink from '@/components/motion/transition-link'
import { pricing } from '@/constants/content'

interface PricingProps {
    index?: string
}

// "Which option fits your business?" On wide screens the column under the
// pointer widens to fit its content while the others give way.
const Pricing: FC<PricingProps> = ({ index = '09' }): ReactNode => {
    return (
        <section id='pricing' data-scene='ink' aria-labelledby='pricing-title' className='px-5 py-28 md:px-8 md:py-44'>
            <div className='grid gap-8 lg:grid-cols-12'>
                <div className='lg:col-span-3'>
                    <SectionLabel index={index}>
                        Pricing
                    </SectionLabel>
                </div>

                <div className='lg:col-span-9'>
                    <SplitReveal as='h2' id='pricing-title' className='type-h1'>
                        Which option <em className='type-serif text-pop'>fits</em> your business?
                    </SplitReveal>

                    <p className='type-lead mt-8 max-w-[46ch] text-quiet'>
                        {"Choose a focused new website, a full redesign or ongoing improvements. We'll agree the pages, deliverables and price before work begins."}
                    </p>
                </div>
            </div>

            <div className='mt-16 flex flex-col border-t border-rule lg:mt-24 lg:flex-row lg:border-t-0'>
                {pricing.map((plan, planIndex) => (
                    <article
                        key={plan.name}
                        data-theme={planIndex === 1 ? 'blue' : undefined}
                        className={cn(
                            'flex flex-col border-b border-rule py-12 transition-[flex-grow] duration-700 ease-quart lg:flex-1 lg:border-b-0 lg:px-8 lg:py-10 lg:hover:grow-[1.5] motion-reduce:transition-none',
                            planIndex === 1 ? 'bg-canvas px-6' : 'lg:border-l',
                            planIndex === pricing.length - 1 && 'lg:border-r'
                        )}
                    >
                        <div className='flex items-baseline justify-between gap-4'>
                            <h3 className='type-h3'>
                                {plan.name}
                            </h3>

                            <span className='type-label text-quiet'>
                                0{planIndex + 1}
                            </span>
                        </div>

                        <p className='mt-12 flex items-baseline gap-2'>
                            <span className='type-label text-quiet'>
                                From
                            </span>

                            <span className='font-heading text-[clamp(3rem,5.5vw,6rem)] font-[680] leading-none tracking-[-0.05em] tabular-nums [font-variation-settings:"wdth"_110]'>
                                {plan.price}
                            </span>

                            {plan.period && (
                                <span className='type-label text-quiet'>
                                    {plan.period}
                                </span>
                            )}
                        </p>

                        {plan.note && (
                            <p className='type-label mt-4 text-pop'>
                                {plan.note}
                            </p>
                        )}

                        <p className='mt-8 max-w-[38ch] text-quiet'>
                            {plan.description}
                        </p>

                        <ul className='mt-10 space-y-3 border-t border-rule pt-8'>
                            {plan.includes.map(item => (
                                <li key={item} className='flex gap-3'>
                                    <Plus aria-hidden='true' className='mt-1 size-4 shrink-0 text-pop' />
                                    {item}
                                </li>
                            ))}
                        </ul>

                        <TransitionLink
                            href={`/contact?service=${plan.service}`}
                            className={cn(
                                'group mt-12 inline-flex w-fit items-center gap-3 px-5 py-4 text-sm font-medium lg:mt-auto',
                                planIndex === 1 ? 'bg-volt text-ink' : 'border border-fore'
                            )}
                        >
                            <RollText>
                                {plan.cta}
                            </RollText>

                            <ArrowUpRight aria-hidden='true' className='size-4' />
                        </TransitionLink>
                    </article>
                ))}
            </div>

            <p className='mt-12 text-quiet'>
                Looking for something else?{' '}
                <TransitionLink href='/contact' className='link-line text-fore'>
                    Tell us what you need
                </TransitionLink>
                .
            </p>
        </section>
    )
}

export default Pricing
